import { useEffect, useRef } from 'react'
import Collapse from 'react-bootstrap/Collapse'
import Form from 'react-bootstrap/Form'
import { useSignal } from '@preact/signals-react'

import { isApiInterfaceKeyField } from '../configTemplateSchemaUtils'

function TriStateCheckbox({ checked, indeterminate, onChange, className }) {
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate
    }, [indeterminate])
    return (
        <Form.Check
            ref={ref}
            type="checkbox"
            label={null}
            checked={checked}
            onChange={onChange}
            className={className}
        />
    )
}

function GroupCheckbox({ checked, indeterminate, label, onChange, className }) {
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate
    }, [indeterminate])
    return (
        <Form.Check
            ref={ref}
            type="checkbox"
            label={label}
            checked={checked}
            onChange={onChange}
            className={className}
        />
    )
}

function CollapseArrow({ isCollapsed, onClick }) {
    return (
        <span
            onClick={e => { e.preventDefault(); e.stopPropagation(); onClick() }}
            style={{
                display: 'inline-block',
                width: '1rem',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                verticalAlign: 'middle',
                marginRight: '2px',
                color: '#000',
            }}
        >
            {isCollapsed ? '▶' : '▼'}
        </span>
    )
}

function getModelName(itemSchema) {
    for (const field of Object.values(itemSchema.properties || {})) {
        if (field['s4-model-name']) return field['s4-model-name']
    }
    return null
}

export default function ConfigTemplateApiInterfaceChecklist({ schema, checkedFieldsSignal }) {

    const items = schema?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items

    // Start every section expanded — this list is scoped to the single interface type
    // being edited, so collapsing it would hide the whole thing. Sections remain
    // collapsible by hand via the arrow.
    const collapsedSignal = useSignal({})

    if (!Array.isArray(items) || items.length === 0) return null

    const checkedPaths = checkedFieldsSignal.value

    const toggleSection = (key) => {
        collapsedSignal.value = { ...collapsedSignal.value, [key]: !collapsedSignal.value[key] }
    }

    const handleFieldToggle = (dotPath, isChecked) => {
        const next = new Set(checkedPaths)
        if (isChecked) next.add(dotPath)
        else next.delete(dotPath)
        checkedFieldsSignal.value = next
    }

    const handleGroupToggle = (paths, isChecked) => {
        const next = new Set(checkedPaths)
        for (const p of paths) {
            if (isChecked) next.add(p)
            else next.delete(p)
        }
        checkedFieldsSignal.value = next
    }

    return (
        <div className="config-template-checklist" style={{ fontSize: '0.85rem' }}>
            {items.map(item => {
                const modelName = getModelName(item)
                if (!modelName) return null

                const displayName = Object.values(item.properties || {}).find(f => f['s4-model-name'])?.['s4-caption'] || modelName
                const s4Groups = item['s4-groups'] || []
                const groupMap = {}
                for (const g of s4Groups) {
                    groupMap[g.id] = { id: g.id, label: g.displayName, paths: [], fields: [] }
                }
                groupMap['__ungrouped__'] = { id: '__ungrouped__', label: null, paths: [], fields: [] }

                for (const [fieldKey, field] of Object.entries(item.properties || {})) {
                    if (field['s4-display'] === false) continue
                    // cdType and taskDesc are the server-side merge key; always saved, never optional
                    if (isApiInterfaceKeyField(fieldKey, field)) continue
                    const dotPath = `apiInterface.${modelName}.${fieldKey}`
                    const caption = field['s4-caption'] || fieldKey
                    const groupId = field['s4-group-id'] || '__ungrouped__'
                    const bucket = groupMap[groupId] || groupMap['__ungrouped__']
                    bucket.paths.push(dotPath)
                    bucket.fields.push({ dotPath, caption })
                }

                const groups = [
                    ...s4Groups.map(g => groupMap[g.id]).filter(g => g.fields.length > 0),
                    ...(groupMap['__ungrouped__'].fields.length > 0 ? [groupMap['__ungrouped__']] : [])
                ]
                const sectionPaths = groups.flatMap(g => g.paths)
                if (sectionPaths.length === 0) return null

                const checkedCount = sectionPaths.filter(p => checkedPaths.has(p)).length
                const allChecked = checkedCount === sectionPaths.length
                const indeterminate = checkedCount > 0 && !allChecked
                const isCollapsed = !!collapsedSignal.value[modelName]

                return (
                    <div key={modelName} className="mb-3">
                        <div className="d-flex align-items-center">
                            <TriStateCheckbox
                                checked={allChecked}
                                indeterminate={indeterminate}
                                onChange={e => handleGroupToggle(sectionPaths, e.target.checked)}
                                className="mb-0 me-1"
                            />
                            <CollapseArrow isCollapsed={isCollapsed} onClick={() => toggleSection(modelName)} />
                            <strong style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSection(modelName)}>
                                {displayName}
                            </strong>
                        </div>
                        <Collapse in={!isCollapsed}>
                            <div style={{ paddingLeft: '1.5rem' }}>
                                {groups.map(group => {
                                    const groupCheckedCount = group.paths.filter(p => checkedPaths.has(p)).length
                                    const groupAllChecked = groupCheckedCount === group.paths.length
                                    const groupIndeterminate = groupCheckedCount > 0 && !groupAllChecked
                                    return (
                                        <div key={group.id} className="mb-2">
                                            {group.label && (
                                                <GroupCheckbox
                                                    checked={groupAllChecked}
                                                    indeterminate={groupIndeterminate}
                                                    label={<em>{group.label}</em>}
                                                    onChange={e => handleGroupToggle(group.paths, e.target.checked)}
                                                    className="mb-1"
                                                />
                                            )}
                                            <div style={{ paddingLeft: group.label ? '1rem' : 0 }}>
                                                {group.fields.map(({ dotPath, caption }) => (
                                                    <Form.Check
                                                        key={dotPath}
                                                        type="checkbox"
                                                        label={caption}
                                                        checked={checkedPaths.has(dotPath)}
                                                        onChange={e => handleFieldToggle(dotPath, e.target.checked)}
                                                        className="mb-0"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </Collapse>
                    </div>
                )
            })}
        </div>
    )
}
